import { useRecoilValue } from 'recoil';
import { array, number, object, string, boolean } from 'yup';

import { userDispensariesAtom } from 'src/app/state/user-dispensaries';

import type { AutomatedCampaignStatus, CampaignBuilderState, CampaignStatus } from './builder-context';
import type { Option } from 'src/app/components/lib/input/select-adornment';
import type { UtmParameter } from 'src/app/queries/marketing/get-campaigns';
import type { MarketingCampaign } from 'src/app/queries/marketing/upsert-campaign';

// yup schemas
export const oneTimeBlastCampaignSetupSchema = object({
  campaignName: string().required(),
  sendToAll: boolean(),
  selectedSegments: array().when('sendToAll', {
    is: false,
    then: array().min(1).of(number()).required(),
  }),
  emailConfig: object()
    .nullable()
    .shape({
      subjectLine: string().when('isActive', {
        is: (value) => !!value,
        then: string().required(),
      }),
      previewText: string(),
      isActive: boolean(),
      senderEmail: string().nullable().when('senderRequired', {
        is: true,
        then: string().required(),
      }),
      metaSenderEmail: string().nullable().when('senderRequired', {
        is: true,
        then: string().required(),
      }),
    }),
  smsConfig: object()
    .nullable()
    .shape({
      isMMS: boolean()
        .nullable()
        .when('isActive', {
          is: (value) => !!value,
          then: boolean().required(),
        }),
      leafbuyerSMSMarketingConfigId: number()
        .nullable()
        .when('isActive', {
          is: (value) => !!value,
          then: number().required(),
        }),
      leafbuyerMessageTemplateId: string().nullable(),
      leafbuyerImageTemplateId: string().nullable(),
      isActive: boolean(),
    }),
  pushConfig: object()
    .nullable()
    .shape({
      title: string().when('isActive', {
        is: (value) => !!value,
        then: string().required(),
      }),
      body: string().when('isActive', {
        is: (value) => !!value,
        then: string().required(),
      }),
      redirectPath: string().when('isActive', {
        is: (value) => !!value,
        then: string().required(),
      }),
      isActive: boolean(),
    }),
}).test(
  'at-least-one-channel',
  'At least one channel (SMS/Email/Push) must be active',
  (value) => !!(value.emailConfig?.isActive || value.smsConfig?.isActive || value.pushConfig?.isActive)
);

export const automatedCampaignSetupSchema = object({
  campaignName: string().required(),
  sendToAll: boolean(),
  selectedSegments: array().when('sendToAll', {
    is: false,
    then: array().min(1).of(number()).required(),
  }),
  emailConfig: object()
    .nullable()
    .shape({
      subjectLine: string().when('isActive', {
        is: (value) => !!value,
        then: string().required(),
      }),
      previewText: string(),
      isActive: boolean(),
      senderEmail: string().nullable().when('senderRequired', {
        is: true,
        then: string().required(),
      }),
      metaSenderEmail: string().nullable().when('senderRequired', {
        is: true,
        then: string().required(),
      }),
    }),
  smsConfig: object()
    .nullable()
    .shape({
      isMMS: boolean(),
      leafbuyerSMSMarketingConfigId: number()
        .nullable()
        .when('isActive', {
          is: (value) => !!value,
          then: number().required(),
        }),
      leafbuyerMessageTemplateId: string().nullable(),
      leafbuyerImageTemplateId: string().nullable(),
      isActive: boolean(),
    }),
  pushConfig: object()
    .nullable()
    .shape({
      title: string().when('isActive', {
        is: (value) => !!value,
        then: string().required(),
      }),
      body: string().when('isActive', {
        is: (value) => !!value,
        then: string().required(),
      }),
      redirectPath: string().when('isActive', {
        is: (value) => !!value,
        then: string().required(),
      }),
      isActive: boolean(),
    }),
  eventTypeId: number().required(),
  delayInMinutes: number()
    .nullable()
    .when('eventTypeId', {
      is: (eventTypeId) => eventTypeId !== EVENT_TYPE_IDS.ENTERED_SEGMENT,
      then: (schema) => schema.required('Time delay is required').positive().max(1000000),
      otherwise: (schema) => schema.nullable(),
    }),
  delayUnitId: number()
    .nullable()
    .when('eventTypeId', {
      is: (eventTypeId) => eventTypeId !== EVENT_TYPE_IDS.ENTERED_SEGMENT,
      then: (schema) => schema.required('Time unit is required'),
      otherwise: (schema) => schema.nullable(),
    }),
  timeOfDayToSend: string().when('eventTypeId', {
    is: (eventTypeId) => EVENT_TYPE_TIME_OF_DAY_ENABLED_IDS.includes(eventTypeId),
    then: (schema) => schema.required('Time of day to send is required'),
    otherwise: (schema) => schema.nullable(),
  }),
}).test(
  'at-least-one-channel',
  'At least one channel (SMS/Email/Push) must be active',
  (value) => !!(value.emailConfig?.isActive || value.smsConfig?.isActive || value.pushConfig?.isActive)
);

// the 2nd and 3rd steps of the wizard have very UI-specific validation conditions which don't really represent "form data" in
// the same manner as the first/last steps, so I've chosen to check those conditions manually in the components rather than
// making a yup schema for them
export const finalReviewSchema = object({
  campaignName: string().required(),
  selectedSegments: array().of(number()).required(),
  subjectLine: string().required(),
  previewText: string().required(),
  savedTemplateId: number(),
});

// yup validators
export function isOneTimeBlastCampaignSetupValid(campaignSetupState) {
  try {
    oneTimeBlastCampaignSetupSchema.validateSync(campaignSetupState);
    return true;
  } catch (error) {
    return false;
  }
}
// yup validators
export function isAutomatedCampaignSetupValid(campaignSetupState) {
  try {
    automatedCampaignSetupSchema.validateSync(campaignSetupState);
    return true;
  } catch (error) {
    return false;
  }
}

// yup validators
export function isEventTypeSetupValid({ eventTypeId }: { eventTypeId: number | null }): boolean {
  return !!eventTypeId;
}

export function isFinalReviewValid(finalReviewState) {
  try {
    finalReviewSchema.validateSync(finalReviewState);
    return true;
  } catch {
    return false;
  }
}

const getUTCDateFromNewFieldsOrExisting = (newFieldsDate: Date | null | undefined, existingDate: string | null) => {
  if (!newFieldsDate && !existingDate) {
    return null;
  }
  if (newFieldsDate) {
    return newFieldsDate;
  }
  return existingDate ? new Date(existingDate) : null;
};

/**
 * Given some fields to update and the existing Campaign editor state, compute a campaign payload to be sent to the upsert endpoint
 * @param newFields - The new fields to update the campaign with
 * @param existingState - The existing state of the campaign
 * @returns The payload to send to the server to save the campaign
 */
export const getSaveCampaignPayload = (
  newFields:
    | (Partial<CampaignBuilderState> & {
        newSendDate?: Date | null;
        statusId?: AutomatedCampaignStatus | CampaignStatus;
      })
    | null,
  existingState: CampaignBuilderState
): MarketingCampaign => {
  const utmParameters: UtmParameter[] = [];
  const utmTerm = newFields?.utmTerm ?? existingState.utmTerm;
  if (utmTerm) {
    utmParameters.push({ ParameterName: 'utm_term', ParameterValue: utmTerm });
  }

  const payload: MarketingCampaign = {
    MarketingCampaignId: newFields?.marketingCampaignId ?? existingState.marketingCampaignId,
    CampaignStatusId: newFields?.statusId ?? existingState.statusID,
    CampaignName: newFields?.campaignName ?? existingState.campaignName,
    RecipientSegmentIds: newFields?.selectedSegments ?? existingState.selectedSegments,
    ScheduleSendForUtc:
      getUTCDateFromNewFieldsOrExisting(newFields?.newSendDate, existingState.sendDate)?.toUTCString() ?? null,
    SendToAllCustomers: newFields?.sendToAll ?? existingState.sendToAll,
    LastModifiedDateUTC: newFields?.lastModifiedDateUTC ?? existingState.lastModifiedDateUTC ?? null,
    EventTypeId: newFields?.eventTypeId ?? existingState.eventTypeId ?? null,
    DelayInMinutes: newFields?.delayInMinutes ?? existingState.delayInMinutes ?? null,
    DelayUnitId: newFields?.delayUnitId ?? existingState.delayUnitId ?? null,
    TimeOfDayToSend: newFields?.timeOfDayToSend ?? existingState.timeOfDayToSend ?? null,
    SmsConfig: null,
    EmailConfig: null,
    PushConfig: null,
    PointBoostConfig: null,
    TagIds: newFields?.tagIds ?? existingState.tagIds ?? [],
    CustomUtmParameters: utmParameters,
    IsWaterfall: newFields?.isWaterfall ?? existingState.isWaterfall ?? null,
  };

  const newEmailConfig = newFields?.emailConfig;
  const oldEmailConfig = existingState.emailConfig;
  payload.EmailConfig = {
    MarketingEmailId: oldEmailConfig?.marketingEmailId || null,
    MarketingEmailTemplateId:
      newFields?.savedTemplateId !== undefined ? newFields.savedTemplateId : existingState.savedTemplateId || null, // allows "deleting" an in-progress template by setting id to null
    MarketingTemplatePresetId:
      newEmailConfig?.marketingTemplatePresetId !== undefined
        ? newEmailConfig?.marketingTemplatePresetId
        : oldEmailConfig?.marketingTemplatePresetId || null, // allows "deleting" an in-progress template by setting id to null
    PreviewTextLine: newEmailConfig?.previewText ?? oldEmailConfig?.previewText ?? '',
    SubjectLine: newEmailConfig?.subjectLine ?? oldEmailConfig?.subjectLine ?? '',
    ReplyToEmail: null,
    IsActive: newEmailConfig?.isActive ?? oldEmailConfig?.isActive ?? false,
    SenderEmail: newEmailConfig?.senderEmail ?? oldEmailConfig?.senderEmail ?? null,
    MetaSenderEmail: newEmailConfig?.metaSenderEmail ?? oldEmailConfig?.metaSenderEmail ?? null,
    Priority: newEmailConfig?.priority ?? oldEmailConfig?.priority ?? null,
    CustomUtmParameters: utmParameters,
  };

  const newSmsConfig = newFields?.smsConfig;
  const oldSmsConfig = existingState.smsConfig;
  payload.SmsConfig = {
    MarketingSMSId: oldSmsConfig?.marketingSMSId || null,
    MarketingEmailTemplateId:
      newFields?.savedTemplateId !== undefined ? newFields.savedTemplateId : existingState.savedTemplateId || null, // allows "deleting" an in-progress template by setting id to null
    MarketingTemplatePresetId:
      newSmsConfig?.marketingTemplatePresetId !== undefined
        ? newSmsConfig?.marketingTemplatePresetId
        : oldSmsConfig?.marketingTemplatePresetId || null, // allows "deleting" an in-progress template by setting id to null
    LeafbuyerSMSMarketingConfigId:
      newSmsConfig?.leafbuyerSMSMarketingConfigId ?? oldSmsConfig?.leafbuyerSMSMarketingConfigId ?? null,
    LeafbuyerMessageTemplateId:
      newSmsConfig?.leafbuyerMessageTemplateId ?? oldSmsConfig?.leafbuyerMessageTemplateId ?? null,
    LeafbuyerImageTemplateId: newSmsConfig?.leafbuyerImageTemplateId ?? oldSmsConfig?.leafbuyerImageTemplateId ?? null,
    IsActive: newSmsConfig?.isActive ?? oldSmsConfig?.isActive ?? false,
    IsMMS: newSmsConfig?.isMMS ?? oldSmsConfig?.isMMS ?? false,
    Priority: newSmsConfig?.priority ?? oldSmsConfig?.priority ?? null,
    CustomUtmParameters: utmParameters,
  };

  const newPushConfig = newFields?.pushConfig;
  const oldPushConfig = existingState.pushConfig;
  payload.PushConfig = {
    MarketingPushId: oldPushConfig?.marketingPushId || null,
    Title: newPushConfig?.title ?? oldPushConfig?.title ?? '',
    Body: newPushConfig?.body ?? oldPushConfig?.body ?? '',
    RedirectPath: newPushConfig?.redirectPath ?? oldPushConfig?.redirectPath ?? '',
    IsActive: newPushConfig?.isActive ?? oldPushConfig?.isActive ?? false,
    Priority: newPushConfig?.priority ?? oldPushConfig?.priority ?? null,
  };

  const newPointBoostConfig = newFields?.pointBoostConfig;
  const oldPointBoostConfig = existingState.pointBoostConfig;
  payload.PointBoostConfig = {
    Points: newPointBoostConfig?.points ?? oldPointBoostConfig?.points ?? 0,
  };

  return payload;
};
export function formatTimeAgo(utcDateString: string) {
  const now = new Date();
  const targetDate = new Date(`${utcDateString}Z`);
  const diffInSeconds = Math.floor((now.valueOf() - targetDate.valueOf()) / 1000);

  // Helper function to pad single digit numbers with leading zeros
  function pad(num: number): string {
    return num.toString().padStart(2, '0');
  }

  // Less than 15 sec ago
  if (diffInSeconds < 15) {
    return 'a few seconds ago';
  }

  // For longer than 15 sec, return the full date and time
  const day = pad(targetDate.getDate());
  const month = pad(targetDate.getMonth() + 1); // Months are zero-indexed
  const year = targetDate.getFullYear();
  const hours = targetDate.getHours();
  const minutes = pad(targetDate.getMinutes());
  const amPm = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 === 0 ? 12 : hours % 12;

  return `${month}/${day}/${year} at ${formattedHours}:${minutes} ${amPm}`;
}

// time delay and unit options
export enum TimeUnitEnum {
  milliseconds = 1,
  seconds = 2,
  minutes = 3,
  hours = 4,
  days = 5,
  week = 6,
  month = 7,
  year = 8,
}

export type TimeUnit = keyof typeof TimeUnitEnum;

export const TIME_UNIT_CONVERSION = {
  minutes: 1,
  hours: 60,
  days: 1440,
};

export const EVENT_TYPE_IDS = {
  POST_PURCHASE: 1,
  NEW_CUSTOMER: 2,
  ABANDONED_CART: 3,
  UPCOMING_BIRTHDAY: 4,
  LOYALTY_ENROLLMENT: 5,
  LOYALTY_POINTS_EXPIRATION: 6,
  ENTERED_SEGMENT: 7,
};

export const EVENT_TYPE_TIME_OF_DAY_ENABLED_IDS = [EVENT_TYPE_IDS.UPCOMING_BIRTHDAY, EVENT_TYPE_IDS.ENTERED_SEGMENT];

// sans upcoming birthday and entered segment
export const EVENT_TYPE_TIME_DELAY_ENABLED_IDS = [
  EVENT_TYPE_IDS.ABANDONED_CART,
  EVENT_TYPE_IDS.LOYALTY_ENROLLMENT,
  EVENT_TYPE_IDS.LOYALTY_POINTS_EXPIRATION,
  EVENT_TYPE_IDS.NEW_CUSTOMER,
  EVENT_TYPE_IDS.POST_PURCHASE,
];

// only upcoming birthday
export const EVENT_TYPE_WHEN_TO_SEND_ENABLED_IDS = [EVENT_TYPE_IDS.UPCOMING_BIRTHDAY];

export const EVENT_TYPE_TIME_UNIT_OPTIONS: Record<string, Option[]> = {
  [EVENT_TYPE_IDS.ABANDONED_CART]: [
    { label: 'Hours', value: String(TimeUnitEnum.hours) },
    { label: 'Days', value: String(TimeUnitEnum.days) },
  ],
  [EVENT_TYPE_IDS.UPCOMING_BIRTHDAY]: [{ label: 'Days', value: String(TimeUnitEnum.days) }],
  default: [
    { label: 'Minutes', value: String(TimeUnitEnum.minutes) },
    { label: 'Hours', value: String(TimeUnitEnum.hours) },
    { label: 'Days', value: String(TimeUnitEnum.days) },
  ],
};

export function getTimeDelayOptions(eventTypeId: number | null): Option[] {
  if (!eventTypeId) {
    return EVENT_TYPE_TIME_UNIT_OPTIONS.default;
  }
  if (eventTypeId === EVENT_TYPE_IDS.UPCOMING_BIRTHDAY) {
    return EVENT_TYPE_TIME_UNIT_OPTIONS[EVENT_TYPE_IDS.UPCOMING_BIRTHDAY];
  }
  return EVENT_TYPE_TIME_UNIT_OPTIONS[eventTypeId] || EVENT_TYPE_TIME_UNIT_OPTIONS.default;
}

export function convertMinutesToUnit(minutes: number, unit: number): number {
  return minutes / TIME_UNIT_CONVERSION[TimeUnitEnum[unit]];
}

export function convertUnitToMinutes(value: number, unit: number): number {
  return value * TIME_UNIT_CONVERSION[TimeUnitEnum[unit]];
}

export function useGetNumberOfConfiguredChannels() {
  const { locProfile } = useRecoilValue(userDispensariesAtom);
  const emailEnabled = locProfile.UseEmailMarketing ? 1 : 0;
  const smsEnabled = locProfile.UseSMSMarketing ? 1 : 0;
  const pushEnabled = locProfile.UsePushMarketing ? 1 : 0;
  return emailEnabled + smsEnabled + pushEnabled;
}
